/**
 * Sign In Page
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStatus } from '../contexts/StatusContext';
import { authApi } from '../api/auth';
import { COGNITO_CONFIG } from '../api/config';
import { startOAuth } from '../api/oauth';
import LocalEnvironmentBadge from '../components/LocalEnvironmentBadge';

type Mode = 'signin' | 'forgot' | 'reset';

const SignIn: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [mode, setMode] = useState<Mode>('signin');
  const [message, setMessage] = useState('');
  const [localError, setLocalError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { authenticate, error } = useStatus();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLocalError('');

    try {
      await authenticate(email, password);
      navigate('/dashboard');
    } catch (err) {
      console.error('Sign in failed:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLocalError('');

    const result = await authApi.forgotPassword(email);
    if (result.success) {
      setMessage('Verification code sent to your email.');
      setMode('reset');
    } else {
      setLocalError(result.error || 'Failed to send reset code');
    }
    setIsSubmitting(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLocalError('');

    const result = await authApi.confirmForgotPassword(email, resetCode, newPassword);
    if (result.success) {
      setMessage('Password reset successfully. Please sign in.');
      setMode('signin');
      setPassword('');
      setResetCode('');
      setNewPassword('');
    } else {
      setLocalError(result.error || 'Failed to reset password');
    }
    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      {/* Authorized Use Banner */}
      <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2">
        <p className="text-center text-xs text-yellow-800">
          This system is for authorized use only. By signing in, you agree to our{' '}
          <a href="https://mindmeld.dev/terms.html" className="underline hover:text-yellow-900" target="_blank" rel="noopener noreferrer">
            Terms of Service
          </a>{' '}
          and{' '}
          <a href="https://mindmeld.dev/privacy.html" className="underline hover:text-yellow-900" target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>.
        </p>
      </div>

      <div className="flex-1 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h1 className="text-center text-4xl font-bold text-mindmeld-primary">MindMeld</h1>
            <div className="mt-2 text-center"><LocalEnvironmentBadge /></div>
            <h2 className="mt-6 text-center text-2xl font-bold text-gray-900">
              Sign in to your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Access your team's AI memory and standards
            </p>
          </div>

        {(error || localError) && (
          <div className="mt-4 rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{localError || error}</div>
          </div>
        )}

        {message && (
          <div className="mt-4 rounded-md bg-green-50 p-4">
            <div className="text-sm text-green-700">{message}</div>
          </div>
        )}

        {mode === 'signin' && (
          <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
            {/* Google OAuth */}
            <div>
              <button
                type="button"
                onClick={() => { startOAuth(COGNITO_CONFIG.googleEnabled ? 'Google' : undefined).catch(() => setLocalError('Could not start sign-in. Use the configured application address.')); }}
                className="w-full flex items-center justify-center gap-3 py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mindmeld-primary"
              >
                {COGNITO_CONFIG.googleEnabled && <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>}
                {COGNITO_CONFIG.googleEnabled ? 'Continue with Google' : 'Continue with hosted sign-in'}
              </button>
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-100 text-gray-500">or</span>
              </div>
            </div>

            <div className="rounded-md shadow-sm space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-mindmeld-primary focus:border-mindmeld-primary sm:text-sm"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-mindmeld-primary focus:border-mindmeld-primary sm:text-sm"
                  placeholder="Password"
                />
              </div>
            </div>

            <div className="flex items-center justify-end">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setLocalError(''); setMessage(''); }}
                className="text-sm text-mindmeld-primary hover:text-blue-700"
              >
                Forgot your password?
              </button>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-mindmeld-primary hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-mindmeld-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </button>
            </div>

            <div className="text-center text-sm space-y-2">
              <p className="text-gray-600">
                Don't have an account?{' '}
                <a href="https://mindmeld.dev/#pricing" className="text-mindmeld-primary hover:text-blue-700 font-medium">
                  Get started
                </a>
              </p>
              <a
                href="https://mindmeld.dev"
                className="text-gray-500 hover:text-gray-700 block text-xs"
              >
                Back to homepage
              </a>
              <div className="text-gray-500 text-xs">
                <a href="https://mindmeld.dev/terms.html" className="hover:underline" target="_blank" rel="noopener noreferrer">Terms</a>
                {' · '}
                <a href="https://mindmeld.dev/privacy.html" className="hover:underline" target="_blank" rel="noopener noreferrer">Privacy</a>
              </div>
            </div>
          </form>
        )}

        {mode === 'forgot' && (
          <form className="mt-8 space-y-6" onSubmit={handleForgotPassword}>
            <p className="text-sm text-gray-600">
              Enter your email address and we'll send you a verification code to reset your password.
            </p>
            <div>
              <label htmlFor="reset-email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <input
                id="reset-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-mindmeld-primary focus:border-mindmeld-primary sm:text-sm"
                placeholder="you@example.com"
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => { setMode('signin'); setLocalError(''); setMessage(''); }}
                className="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to sign in
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-mindmeld-primary hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Sending...' : 'Send code'}
              </button>
            </div>
          </form>
        )}

        {mode === 'reset' && (
          <form className="mt-8 space-y-6" onSubmit={handleResetPassword}>
            <p className="text-sm text-gray-600">
              Enter the verification code sent to <strong>{email}</strong> and your new password.
            </p>
            <div className="space-y-4">
              <div>
                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                  Verification code
                </label>
                <input
                  id="code"
                  type="text"
                  required
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-mindmeld-primary focus:border-mindmeld-primary sm:text-sm"
                  placeholder="123456"
                />
              </div>
              <div>
                <label htmlFor="new-password" className="block text-sm font-medium text-gray-700">
                  New password
                </label>
                <input
                  id="new-password"
                  type="password"
                  required
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="mt-1 appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-mindmeld-primary focus:border-mindmeld-primary sm:text-sm"
                  placeholder="New password"
                />
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="button"
                onClick={() => { setMode('signin'); setLocalError(''); setMessage(''); }}
                className="flex-1 py-2 px-4 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Back to sign in
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-mindmeld-primary hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Resetting...' : 'Reset password'}
              </button>
            </div>
          </form>
        )}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
